/**
 * Formats a date as DD/MM/YYYY to match Figma design
 * @param date - Date object, date string, or null
 * @returns Formatted date string as DD/MM/YYYY or empty string if date is null/invalid
 */
export const formatDateToDDMMYYYY = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  
  let dateObj: Date
  
  // If it's already a Date object, use it
  if (date instanceof Date) {
    dateObj = date
  } else {
    // Try to parse the string
    // Handle various formats: "dd-mm-yyyy", "dd/mm/yyyy", "yyyy-mm-dd", "DD MMM YYYY", etc.
    const dateStr = String(date).trim()
    
    // If it's already in DD/MM/YYYY format, return as is (after validation)
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      // Validate the date
      const parts = dateStr.split('/')
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      const testDate = new Date(year, month - 1, day)
      if (
        testDate.getFullYear() === year &&
        testDate.getMonth() === month - 1 &&
        testDate.getDate() === day
      ) {
        return dateStr
      }
    }
    
    // Try parsing as ISO date (yyyy-mm-dd)
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      dateObj = new Date(dateStr)
    }
    // Try parsing dates with dashes (dd-mm-yyyy)
    else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const parts = dateStr.split('-')
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      dateObj = new Date(year, month - 1, day)
    }
    // Try parsing dates with slashes (could be MM/DD/YYYY or DD/MM/YYYY)
    else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const parts = dateStr.split('/')
      // Assume DD/MM/YYYY format (European format)
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      dateObj = new Date(year, month - 1, day)
    }
    // Try parsing common date formats like "12 Apr 2025"
    else if (/^\d{1,2}\s+\w+\s+\d{4}$/.test(dateStr)) {
      dateObj = new Date(dateStr)
    }
    // Fallback to standard Date parsing
    else {
      dateObj = new Date(dateStr)
    }
  }
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return ''
  }
  
  // Format as DD/MM/YYYY
  const day = String(dateObj.getDate()).padStart(2, '0')
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const year = dateObj.getFullYear()
  
  return `${day}/${month}/${year}`
}

/**
 * Formats a Date object to DD/MM/YYYY format
 * @param date - Date object or null
 * @returns Formatted date string as DD/MM/YYYY
 */
export const formatDate = (date: Date | null): string => {
  if (!date) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

/**
 * Formats a date as "DD MMM YYYY" (e.g., "12 Apr 2025")
 * @param date - Date object, date string, or null
 * @returns Formatted date string as "DD MMM YYYY" or empty string if date is null/invalid
 */
export const formatDateToDDMMMYYYY = (date: Date | string | null | undefined): string => {
  if (!date) return ''
  
  let dateObj: Date
  
  // If it's already a Date object, use it
  if (date instanceof Date) {
    dateObj = date
  } else {
    // Try to parse the string
    const dateStr = String(date).trim()
    
    // If it's already in "DD MMM YYYY" format, return as is
    if (/^\d{1,2}\s+\w+\s+\d{4}$/.test(dateStr)) {
      return dateStr
    }
    
    // Try parsing as ISO date (yyyy-mm-dd)
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      dateObj = new Date(dateStr)
    }
    // Try parsing dates with dashes (dd-mm-yyyy)
    else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const parts = dateStr.split('-')
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      dateObj = new Date(year, month - 1, day)
    }
    // Try parsing dates with slashes (DD/MM/YYYY)
    else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
      const parts = dateStr.split('/')
      const day = parseInt(parts[0], 10)
      const month = parseInt(parts[1], 10)
      const year = parseInt(parts[2], 10)
      dateObj = new Date(year, month - 1, day)
    }
    // Fallback to standard Date parsing
    else {
      dateObj = new Date(dateStr)
    }
  }
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return ''
  }
  
  // Format as "DD MMM YYYY"
  const day = String(dateObj.getDate())
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const month = months[dateObj.getMonth()]
  const year = dateObj.getFullYear()
  
  return `${day} ${month} ${year}`
}
